import { CYLINDO_ACCOUNT_ID } from './constants';

// Default Cylindo frame-1 render width. The interactive viewer's `slot="placeholder"`
// image requests `size=1024`; keep this the single source of truth so the server-resolved
// preload URL (CON-8293) stays byte-identical to what the client paints.
export const CYLINDO_FRAME_ONE_SIZE = 1024;

// Builds the `feature=KEY:VALUE&feature=...` query fragment for a Cylindo frame URL.
// `features` is an ordered object (e.g. { UPHOLSTERY: code, ORIENTATION: code }); insertion
// order is preserved so the produced string is deterministic. Both key and value are
// `encodeURIComponent`-encoded (carried over from develop's encoding fix on the old inline
// param - keys/values can contain spaces or other special characters); for the upholstery/
// orientation codes in use this is byte-identical to the historical `%20`-only output, so the
// SSR preload still matches the viewer placeholder. Returns `null` when there are no features,
// which the frame-1 template concatenates literally (`...&size=1024&null`) - retained for exact
// parity with the historical client output.
export const buildCylindoFrameOneFeaturesParam = features => {
  const entries = Object.entries(features || {});

  if (!entries.length) {
    return null;
  }

  return entries
    .map(
      ([key, value]) =>
        `feature=${encodeURIComponent(key)}:${encodeURIComponent(value)}`
    )
    .join('&');
};

// Single source of truth for the Cylindo frame-1 image URL. Used by both the client
// `PDPCylindo3dViewer` placeholder and the server-side `getProductCylindoHeroImage` resolver
// (CON-8293) so the SSR-painted/preloaded image and the post-hydration viewer placeholder are
// the exact same URL - no double-fetch.
const buildCylindoFrameOneUrl = ({
  productId,
  features,
  size = CYLINDO_FRAME_ONE_SIZE,
} = {}) => {
  const id = productId?.toString();
  const featuresParam = buildCylindoFrameOneFeaturesParam(features);

  return `https://content.cylindo.com/api/v2/${CYLINDO_ACCOUNT_ID}/products/${id}/frames/1/${id}.webp?encoding=webp-100&size=${size}&${featuresParam}`;
};

export default buildCylindoFrameOneUrl;
